import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, Clock, MessageCircle, ArrowRight, Star, 
  Calendar, Dumbbell, Users, ChevronLeft, ChevronRight,
  Quote, Play
} from 'lucide-react';
import { ClassWidget } from '../components/ClassWidget';
import { BookingDrawer } from '../components/BookingDrawer';
import { Button } from '../components/ui/button';
import { 
  COACHES, MEMBERSHIPS, TESTIMONIALS, HOW_IT_WORKS, 
  getWhatsAppLink 
} from '../data/mockData';

export const Home = () => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleBookClass = (classData) => {
    setSelectedClass(classData);
    setIsDrawerOpen(true);
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="container-app">
          {/* Trust Line */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mb-8 animate-fade-in">
            <span className="trust-badge">
              <Clock className="w-4 h-4 text-[#16263B]" />
              Reserva en 30s
            </span>
            <span className="trust-badge">
              <CheckCircle className="w-4 h-4 text-[#16263B]" />
              Cancelación fácil
            </span>
            <span className="trust-badge">
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              Confirmación por WhatsApp
            </span>
          </div>

          {/* Hero Title */}
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tighter mb-4">
              Tu próxima clase
              <br />
              <span className="text-slate-400">te espera</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto">
              Reserva tu lugar en segundos. Sin complicaciones.
            </p>
          </div>

          {/* Class Widget - THE HERO */}
          <div className="max-w-4xl mx-auto mb-8">
            <ClassWidget onBookClass={handleBookClass} isLoading={isLoading} />
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3">
            <Link to="/memberships">
              <Button className="btn-primary w-full sm:w-auto" data-testid="cta-free-trial">
                Prueba gratis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
              data-testid="cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Navigation Cards */}
      <section className="section-spacing bg-white" data-testid="nav-cards-section">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                title: 'Membresías', 
                description: 'Planes flexibles para todos',
                icon: Dumbbell,
                link: '/memberships',
                testId: 'nav-card-memberships'
              },
              { 
                title: 'Horarios', 
                description: 'Encuentra tu clase ideal',
                icon: Calendar,
                link: '/schedule',
                testId: 'nav-card-schedule'
              },
              { 
                title: 'Coaches', 
                description: 'Conoce a nuestro equipo',
                icon: Users,
                link: '/coaches',
                testId: 'nav-card-coaches'
              },
            ].map((card, index) => (
              <Link
                key={card.title}
                to={card.link}
                className={`card-premium p-8 group animate-fade-in-up stagger-${index + 1}`}
                data-testid={card.testId}
              >
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 
                              group-hover:bg-[#16263B] transition-colors duration-300">
                  <card.icon className="w-7 h-7 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                  {card.title}
                </h3>
                <p className="text-slate-500 mb-4">{card.description}</p>
                <span className="inline-flex items-center text-sm font-semibold text-slate-900 
                               group-hover:text-[#16263B] transition-colors">
                  Explorar
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-slate-50" data-testid="how-it-works-section">
        <div className="container-app">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
              Así de fácil
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Cómo funciona
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {HOW_IT_WORKS.map((item, index) => (
              <div 
                key={item.step}
                className={`text-center animate-fade-in-up stagger-${index + 1}`}
              >
                <div className="w-16 h-16 bg-white rounded-2xl shadow-soft flex items-center justify-center mx-auto mb-6">
                  <span className="font-heading font-bold text-2xl text-[#16263B]">{item.step}</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="section-spacing bg-white" data-testid="testimonials-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
              Historias reales
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Lo que dicen nuestros miembros
            </h2>
          </div>

          <div className="relative max-w-3xl mx-auto">
            {/* Carousel */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
              >
                {TESTIMONIALS.map((testimonial) => (
                  <div 
                    key={testimonial.id}
                    className="w-full flex-shrink-0 px-4"
                  >
                    <div className="testimonial-card text-center">
                      <Quote className="w-10 h-10 text-[#16263B] mx-auto mb-6" />
                      <p className="text-lg md:text-xl text-slate-700 mb-6 leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center justify-center gap-4">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                        <div className="text-left">
                          <p className="font-semibold text-slate-900">{testimonial.name}</p>
                          <p className="text-sm text-slate-500">{testimonial.goal}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-1 mt-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#16263B] text-[#16263B]" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setTestimonialIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === testimonialIndex ? 'w-8 bg-[#16263B]' : 'bg-slate-200'
                    }`}
                    data-testid={`testimonial-dot-${idx}`}
                  />
                ))}
              </div>
              <button
                onClick={nextTestimonial}
                className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <p className="text-center text-xs text-slate-400 mt-6">
              *Los resultados varían según cada persona
            </p>
          </div>
        </div>
      </section>

      {/* Plans Preview */}
      <section className="section-spacing bg-slate-50" data-testid="plans-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
              Planes
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Elige tu plan ideal
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {MEMBERSHIPS.map((plan, index) => (
              <div
                key={plan.id}
                className={`membership-card ${plan.popular ? 'featured' : ''} bg-white animate-fade-in-up stagger-${index + 1}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#16263B] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                      Más popular
                    </span>
                  </div>
                )}
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-heading font-bold text-4xl text-slate-900">
                    ${plan.price.toLocaleString()}
                  </span>
                  <span className="text-slate-500">{plan.period}</span>
                </div>
                {plan.originalPrice && (
                  <p className="text-sm text-emerald-600 mb-4">
                    Ahorras ${plan.savings} al año
                  </p>
                )}
                <ul className="space-y-3 mb-6">
                  {plan.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-[#16263B] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/memberships">
                  <Button 
                    className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`plan-cta-${plan.id}`}
                  >
                    {plan.popular ? 'Comenzar ahora' : 'Ver detalles'}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-spacing bg-slate-900" data-testid="final-cta-section">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight mb-6">
            ¿Listo para transformarte?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            Tu primera clase es gratis. Sin compromisos, sin excusas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/memberships">
              <Button className="btn-primary" data-testid="final-cta-trial">
                Inicia tu prueba gratis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
              data-testid="final-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Hablar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950" data-testid="footer">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
            <span className="font-heading font-bold text-[#C9A45C]">FitCenter</span>
            </div>
            <p className="text-sm text-slate-500">
              © 2024 FitCenter. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Booking Drawer */}
      <BookingDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        classData={selectedClass}
      />
    </div>
  );
};
